import { useMemo, useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import Home from './pages/Home';
import { loadGoogleMaps } from "./utils/loadGoogleMaps";  // Corrected path if needed
import OrderHistory from './pages/OrderHistory';
import CreateOrder from './pages/CreateOrder';
import TermsAndConditionsRider from './pages/TermsAndConditionsRider';
import TermsAndConditions from './pages/TermsAndConditions';
import AboutUs from './pages/AboutUs';
import AboutPage from './pages/AboutPage';
import CancellationAndRefund from './pages/CancellationAndRefund';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ResetPassword from './pages/ResetPassword';
import TopUp from './pages/TopUp';
import PrivacyPolicyRider from './pages/PrivacyPolicyRider';
import DeleteAccount from './pages/DeleteAccount';
import Invoice from './pages/Invoice';
import { ToastContainer } from 'react-toastify';
import EditOrder from './pages/EditOrder';
import ApiLanding from './pages/ApiLanding';
import Wallet from './pages/Wallet';
import Notifications from './pages/Notifications';
import NotFound from './pages/NotFound';
import Blogs from './pages/Blogs';
import BlogDetail from './pages/BlogDetail';
import WebsiteNotificationManager from "./components/WebsiteNotificationManager";
import ScrollToTop from "./components/ScrollToTop";
import 'react-toastify/dist/ReactToastify.css';
import {
  CONTACT_EMAIL,
  CONTACT_PHONE,
  breadcrumbSchema,
  faqSchema,
  serviceSchema,
  useSEO,
  webPageSchema,
} from './utils/seo';

const routeSeo = {
  "/": {
    title: "Fast Courier Delivery Service in Mumbai",
    description:
      "Instaport Delivery provides fast, secure and affordable courier, parcel, pickup and drop, bulk order and business delivery services in Mumbai.",
    keywords: [
      "fast courier delivery Mumbai",
      "secure courier delivery",
      "same day courier Mumbai",
      "book courier online",
      "pickup and drop delivery",
    ],
    structuredData: [
      webPageSchema({
        title: "Fast Courier Delivery Service in Mumbai",
        description:
          "Book fast courier, parcel, pickup and drop, bulk order and business delivery services with Instaport Delivery in Mumbai.",
        path: "/",
      }),
      serviceSchema,
      faqSchema([
        {
          question: "What is Instaport Delivery?",
          answer:
            "Instaport Delivery is a Mumbai-based courier and logistics service for fast parcel delivery, pickup and drop, same-day delivery, bulk orders and business delivery needs.",
        },
        {
          question: "How can I contact Instaport customer care?",
          answer: `You can contact Instaport Delivery customer care at ${CONTACT_PHONE} or email ${CONTACT_EMAIL}.`,
        },
        {
          question: "Which delivery services does Instaport provide?",
          answer:
            "Instaport provides courier delivery, parcel delivery, pickup and drop delivery, bulk order delivery and business delivery services.",
        },
      ]),
    ],
  },
  "/about-us": {
    title: "About Instaport Delivery",
    description:
      "Learn what Instaport Delivery is, how our courier delivery platform works, and how we support individuals and businesses with reliable same-day delivery.",
    keywords: [
      "what is Instaport",
      "what is Instaport Delivery",
      "about Instaport",
      "Instaport delivery company",
      "Mumbai logistics platform",
    ],
    structuredData: [
      webPageSchema({
        title: "About Instaport Delivery",
        description:
          "Learn about Instaport Delivery, a courier and logistics platform for individuals and businesses in Mumbai.",
        path: "/about-us",
      }),
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "About Instaport Delivery", path: "/about-us" },
      ]),
      faqSchema([
        {
          question: "What is Instaport?",
          answer:
            "Instaport is the brand behind Instaport Delivery, a courier and delivery service focused on fast, secure and convenient deliveries.",
        },
        {
          question: "What does Instaport Delivery do?",
          answer:
            "Instaport Delivery helps customers and businesses book local courier, parcel, same-day and bulk delivery services.",
        },
      ]),
    ],
  },
  "/contact": {
    title: "Instaport Customer Care and Contact",
    description:
      "Contact Instaport Delivery customer care for courier bookings, delivery support, business delivery queries and general help.",
    keywords: [
      "Instaport customer care",
      "Instaport contact number",
      "Instaport support",
      "Instaport Delivery customer care",
      "courier customer care Mumbai",
    ],
    structuredData: [
      {
        ...webPageSchema({
          title: "Instaport Customer Care and Contact",
          description:
            "Get Instaport Delivery contact details, customer care phone number, email and office address.",
          path: "/contact",
        }),
        "@type": "ContactPage",
      },
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Contact Instaport", path: "/contact" },
      ]),
    ],
  },
  "/create-order": {
    title: "Book Courier Delivery Online",
    description:
      "Book an Instaport courier delivery online for parcel pickup, drop delivery, same-day local delivery, business delivery and multi-stop orders.",
    keywords: [
      "book courier online",
      "online parcel delivery",
      "create courier order",
      "pickup and drop Mumbai",
      "multi stop delivery",
    ],
    structuredData: [
      webPageSchema({
        title: "Book Courier Delivery Online",
        description:
          "Create an Instaport delivery order online for courier, parcel, pickup and drop, business or multi-stop delivery.",
        path: "/create-order",
      }),
      serviceSchema,
    ],
  },
  "/api": {
    title: "Instaport Delivery API for Businesses",
    description:
      "Use the Instaport Delivery API to connect courier booking, parcel delivery and business logistics workflows to your platform.",
    keywords: [
      "delivery API",
      "courier API India",
      "logistics API",
      "Instaport API",
      "business delivery integration",
    ],
    structuredData: [
      webPageSchema({
        title: "Instaport Delivery API for Businesses",
        description:
          "Connect your business platform with Instaport Delivery courier and logistics APIs.",
        path: "/api",
      }),
    ],
  },
  "/privacy-policy": {
    title: "Privacy Policy",
    description:
      "Read the Instaport Delivery privacy policy for customers, delivery partners, website users and app users.",
    structuredData: [
      webPageSchema({
        title: "Privacy Policy",
        description:
          "Instaport Delivery privacy policy for website, app, customer and delivery partner data.",
        path: "/privacy-policy",
      }),
    ],
  },
  "/privacy-policy-rider": {
    title: "Rider Privacy Policy",
    description:
      "Read the Instaport Delivery rider privacy policy for delivery executive partners and service providers.",
    structuredData: [
      webPageSchema({
        title: "Rider Privacy Policy",
        description:
          "Instaport Delivery privacy policy for delivery executive partners and riders.",
        path: "/privacy-policy-rider",
      }),
    ],
  },
  "/terms-and-conditions/customer": {
    title: "Customer Terms and Conditions",
    description:
      "Read the Instaport Delivery customer terms and conditions for courier delivery, payments, cancellation and service use.",
    structuredData: [
      webPageSchema({
        title: "Customer Terms and Conditions",
        description:
          "Instaport Delivery customer terms and conditions for delivery service usage.",
        path: "/terms-and-conditions/customer",
      }),
    ],
  },
  "/terms-and-conditions/rider": {
    title: "Rider Terms and Conditions",
    description:
      "Read the Instaport Delivery rider terms and conditions for delivery executive partners.",
    structuredData: [
      webPageSchema({
        title: "Rider Terms and Conditions",
        description:
          "Instaport Delivery rider terms and conditions for delivery executive partners.",
        path: "/terms-and-conditions/rider",
      }),
    ],
  },
  "/cancellation-and-refund": {
    title: "Cancellation and Refund Policy",
    description:
      "Read the Instaport Delivery cancellation and refund policy for courier delivery orders and service charges.",
    structuredData: [
      webPageSchema({
        title: "Cancellation and Refund Policy",
        description:
          "Instaport Delivery cancellation and refund policy for delivery orders.",
        path: "/cancellation-and-refund",
      }),
    ],
  },
  "/deleteaccount": {
    title: "Delete Instaport Account",
    description:
      "Learn how to request deletion of your Instaport Delivery account and related customer data.",
    noIndex: true,
  },
};

const getSeoForPath = (pathname) => {
  if (
    pathname.startsWith("/orders") ||
    pathname.startsWith("/invoice/") ||
    pathname.startsWith("/edit-order") ||
    pathname.startsWith("/reset-password") ||
    pathname.startsWith("/wallet") ||
    pathname.startsWith("/notifications") ||
    pathname.startsWith("/topup")
  ) {
    return {
      title: "Account Area",
      description:
        "Secure Instaport Delivery account page for customers.",
      path: pathname,
      noIndex: true,
    };
  }

  return routeSeo[pathname] || {
    title: "404 Page Not Found",
    description:
      "The page you are looking for could not be found on Instaport Delivery.",
    path: pathname,
    noIndex: true,
  };
};

const DefaultSEOManager = () => {
  const { pathname } = useLocation();
  const seo = useMemo(() => {
    const pageSeo = getSeoForPath(pathname);
    return {
      ...pageSeo,
      path: pageSeo.path || pathname,
    };
  }, [pathname]);

  useSEO(seo);
  return null;
};

const SEOManager = () => {
  const { pathname } = useLocation();
  if (pathname === "/blogs" || (!routeSeo[pathname] && !/^\/(invoice|edit-order|topup|terms-and-conditions)\//.test(pathname) && /^\/[^/]+\/[a-z0-9]{8}$/i.test(pathname))) return null;
  return <DefaultSEOManager />;
};

function App() {
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [mapsLoading, setMapsLoading] = useState(true);

  useEffect(() => {
    loadGoogleMaps()
      .then((maps) => {
        console.log("Google Maps Loaded", maps);
        setMapsLoaded(true);
      })
      .catch((error) => {
        console.error("Failed to load Google Maps:", error);
      })
      .finally(() => {
        setMapsLoading(false);
      });
  }, []);


  return (
    <>
      {!mapsLoaded && (
        <div className="fixed inset-x-0 top-0 z-[9999] h-1 overflow-hidden bg-transparent">
          <div
            className={`h-full bg-[linear-gradient(90deg,#f8b100_0%,#ffd04f_45%,#1f6d39_100%)] shadow-[0_0_14px_rgba(248,177,0,0.45)] transition-opacity duration-300 ${
              mapsLoading ? "opacity-100" : "opacity-0"
            } animate-[loadingBar_1.2s_ease-in-out_infinite]`}
          />
        </div>
      )}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        closeOnClick
        pauseOnHover={false}
        pauseOnFocusLoss={false}
        draggable
        newestOnTop
        limit={3}
      />
      <Router>
        <ScrollToTop />
        <SEOManager />
        <WebsiteNotificationManager />
        <Routes>
          {/* <Route path="/" element={<ComingSoon />} /> */}
          <Route path="/" element={<Home />} />
          <Route path="/create-order" element={<CreateOrder />} />
          <Route path="/orders" element={<OrderHistory />} />
          <Route path="/invoice/:orderId" element={<Invoice />} />
          <Route path="/about-us" element={<AboutPage />} />
          <Route path="/contact" element={<AboutUs />} />
          <Route path="/edit-order/:orderId" element={<EditOrder />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/wallet" element={<Wallet />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/privacy-policy-rider" element={<PrivacyPolicyRider />} />
          <Route path="/cancellation-and-refund" element={<CancellationAndRefund />} />
          <Route path="/terms-and-conditions/customer" element={<TermsAndConditions />} />
          <Route path="/terms-and-conditions/rider" element={<TermsAndConditionsRider />} />
          <Route path="/deleteaccount" element={<DeleteAccount />} />
          <Route path="/topup/:token" element={<TopUp />} />
          <Route path="/api" element={<ApiLanding />} />
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/:slug/:blogId" element={<BlogDetail />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </>
  );
}

export default App;
